<template>
  <div class="app-page__container">
    <section v-if="!isDashboardDataLoading" class="app-page__header">
      <h5 class="title is-5">Analytics</h5>
    </section>

    <div v-if="!isDashboardDataLoading" class="columns is-paddingless is-desktop">
      <div class="column is-7-desktop">

        <section class="app-page__section">
          <GoalMetrics
            :active-user-id="activeUserId"
            :is-loading="isDashboardDataLoading"
            title="My Goals"
          />
        </section>

      </div>
      <div class="column">
        <section class="app-page__section">
          <TeamMembersHitRates
            :active-date="activeDate"
            :active-team-ids="getUserTeams(activeUserId, teams).map(team => team.id)"
            :excluded-user-ids="[activeUserId]"
            :custom-card-title="getUserTeams(activeUserId, teams)[0].teamName"
          />
        </section>
      </div>
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <BLoading :is-full-page="false" :active.sync="isDashboardDataLoading" :can-cancel="false"></BLoading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { defaultDateOptions, dateToISO } from '@/helpers/dateHelpers';
import { getUserTeams } from '@/helpers/teamHelpers';
import * as UserActions from '@/store/actions/User.actions';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as TeamActions from '@/store/actions/Team.actions';
import GoalMetrics from '@/components/GoalMetrics.vue';
import TeamMembersHitRates from '@/components/TeamMembersHitRates.vue';

export default {
  name: 'AnalyticsView',

  components: {
    GoalMetrics,
    TeamMembersHitRates,
  },

  data() {
    return {
      todaysGoal: '',
      isEditingTodaysGoal: true,
      isActiveUserLoading: true,
      isTodaysGoalLoading: true,
      isNewActiveGoalLoading: false,
      defaultDateOptions,
      areTeammatesLoading: true,
      areUserGoalsLoading: true,
      activeUserId: null,
      activeDate: new Date(),
    };
  },

  computed: {
    ...mapGetters([
      'loggedInUser',
      'activeWorkspace',
    ]),
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      isSessionAuthLoading: state => state.auth.isSessionAuthLoading,
      isLogInFormLoading: state => state.auth.isLogInFormLoading,
      teams: state => state.team.teams,
      users: state => state.user.users,
    }),
    isDashboardDataLoading() {
      return this.isActiveUserLoading || this.isTodaysGoalLoading;
    },
    activeUser() {
      return this.users[this.activeUserId] || {};
    },
    isActiveUserLoggedInUser() {
      if (!this.activeUserId || !this.loggedInUser.id) return false;

      return this.activeUserId === this.loggedInUser.id;
    },
    activeDateInISO() {
      return dateToISO(this.activeDate);
    },
  },

  created() {
    this.fetchInitialData();
  },

  watch: {
    '$route'() { // eslint-disable-line object-shorthand
      this.fetchInitialData();
    },
  },

  methods: {
    getUserTeams,
    ...mapActions({
      fetchWorkspaceUsersById: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchUserTeams: TeamActions.FETCH_USER_TEAMS,
      fetchPublicUserTeams: TeamActions.FETCH_PUBLIC_USER_TEAMS,
      fetchGoalsByUserIdsByDates: GoalActions.FETCH_GOALS_BY_USER_IDS_BY_DATES,
    }),
    async fetchActiveUserOnLoad() {
      this.activeUserId = this.loggedInUser.id;

      await this.fetchWorkspaceUsersById({ userIds: [this.activeUserId], workspaceId: this.activeWorkspace.id });
      if (this.isActiveUserLoggedInUser) {
        await this.fetchUserTeams({ userId: this.activeUserId, workspaceId: this.activeWorkspace.id });
      } else {
        await this.fetchPublicUserTeams({ userId: this.activeUserId, workspaceId: this.activeWorkspace.id });
      }
    },
    async fetchInitialData() {
      this.isActiveUserLoading = true;
      await this.fetchActiveUserOnLoad();
      this.isActiveUserLoading = false;

      this.isTodaysGoalLoading = true;
      await this.fetchGoalsByUserIdsByDates({
        userIds: [this.activeUserId],
        dates: [this.activeDateInISO],
        workspaceId: this.activeWorkspace.id,
      });
      this.isTodaysGoalLoading = false;
    },
    async handleActiveDateChange(newDate) {
      this.activeDate = newDate;
      this.isNewActiveGoalLoading = true;
      await this.fetchGoalsByUserIdsByDates({
        userIds: [this.activeUserId],
        dates: [this.activeDateInISO],
        workspaceId: this.activeWorkspace.id,
      });
      this.isNewActiveGoalLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-details__container {
  display: flex;
}

.user-details__name {
  margin-left: $global-whitespace-small;
  align-self: center;
}

.user-name {
  align-self: center;
  margin-left: $global-whitespace-small;
}

.content-wrapper {
  display: flex;
}
</style>
